import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { CATEGORIES, formatPrice } from '../data/components';
import { toast } from 'sonner';
import {
  ArrowLeft, Upload, Save, Trash2, Plus, Edit2, X, Image,
  LogOut, Settings, Package, ChevronLeft, ChevronRight, LayoutGrid,
  History, Palette, FileText
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ITEMS_PER_PAGE = 10;

export default function AdminPanel() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [components, setComponents] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [changeLogs, setChangeLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(null);
  const [editComp, setEditComp] = useState(null);
  const [showEdit, setShowEdit] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [showTemplateEdit, setShowTemplateEdit] = useState(false);
  const [editTemplate, setEditTemplate] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [filterCategory, setFilterCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('components');
  const fileInputRef = useRef(null);
  const glbInputRef = useRef(null);

  const [form, setForm] = useState({
    comp_id: '', name: '', category: 'Seating', subcategory: '',
    grid_w: 2, grid_h: 2, price: 0, weight: 0, description: '', icon: 'box',
    textures: [], colors: [], materials: []
  });

  const [templateForm, setTemplateForm] = useState({
    id: '', name: '', desc: '', budget: 'Mid', price: 0, color: '#3b82f6', components: []
  });

  // Check admin role
  useEffect(() => {
    axios.get(`${API}/auth/role`, { withCredentials: true })
      .then(r => {
        if (!r.data.is_admin) {
          toast.error('Access denied. Admin only.');
          navigate('/studio');
        } else {
          setIsAdmin(true);
        }
      })
      .catch(() => {
        toast.error('Please login to access admin panel');
        navigate('/');
      });
  }, [navigate]);

  useEffect(() => { 
    if (isAdmin) {
      fetchComponents();
      fetchTemplates();
      fetchChangeLogs();
    }
  }, [isAdmin]);

  const fetchComponents = async () => {
    try {
      const r = await axios.get(`${API}/admin/components`, { withCredentials: true });
      setComponents(r.data);
    } catch {
      const r = await axios.get(`${API}/components`);
      setComponents(r.data);
    }
    setLoading(false);
  };

  const fetchTemplates = async () => {
    try {
      const r = await axios.get(`${API}/configurations`);
      setTemplates(r.data);
    } catch (e) {
      console.log('Templates fetch failed:', e);
    }
  };

  const fetchChangeLogs = async () => {
    try {
      const r = await axios.get(`${API}/admin/changelog`, { withCredentials: true });
      setChangeLogs(r.data);
    } catch {
      setChangeLogs([]);
    }
  };

  // Filter and paginate components
  const filteredComponents = components.filter(c => {
    const matchesCategory = filterCategory === 'all' || c.category === filterCategory;
    const matchesSearch = !searchTerm || 
      c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.comp_id.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const totalPages = Math.ceil(filteredComponents.length / ITEMS_PER_PAGE);
  const paginatedComponents = filteredComponents.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const handleEdit = (comp) => {
    setEditComp(comp);
    setForm({
      comp_id: comp.comp_id,
      name: comp.name,
      category: comp.category,
      subcategory: comp.subcategory || '',
      grid_w: comp.grid_w,
      grid_h: comp.grid_h,
      price: comp.price,
      weight: comp.weight,
      description: comp.description || '',
      icon: comp.icon || 'box',
      textures: comp.textures || [],
      colors: comp.colors || [],
      materials: comp.materials || [],
    });
    setShowEdit(true);
  };

  const handleSaveEdit = async () => {
    try {
      await axios.put(`${API}/admin/components/${form.comp_id}`, {
        name: form.name,
        category: form.category,
        subcategory: form.subcategory,
        grid_w: Number(form.grid_w),
        grid_h: Number(form.grid_h),
        price: Number(form.price),
        weight: Number(form.weight),
        description: form.description,
        icon: form.icon,
        textures: form.textures,
        colors: form.colors,
        materials: form.materials,
      }, { withCredentials: true });
      toast.success('Component updated');
      setShowEdit(false);
      fetchComponents();
      fetchChangeLogs();
    } catch (e) {
      toast.error('Update failed: ' + (e.response?.data?.detail || e.message));
    }
  };

  const handleAddNew = async () => {
    try {
      await axios.post(`${API}/admin/components`, {
        ...form,
        grid_w: Number(form.grid_w),
        grid_h: Number(form.grid_h),
        price: Number(form.price),
        weight: Number(form.weight),
      }, { withCredentials: true });
      toast.success('Component added');
      setShowAdd(false);
      fetchComponents();
      fetchChangeLogs();
    } catch (e) {
      toast.error('Add failed: ' + (e.response?.data?.detail || e.message));
    }
  };

  const handleDelete = async (compId) => {
    if (!window.confirm('Delete this component?')) return;
    try {
      await axios.delete(`${API}/admin/components/${compId}`, { withCredentials: true });
      toast.success('Component deleted');
      fetchComponents();
      fetchChangeLogs();
    } catch (e) {
      toast.error('Delete failed');
    }
  };

  const handleImageUpload = async (compId, file) => {
    const fd = new FormData();
    fd.append('file', file);
    try {
      await axios.post(`${API}/admin/components/${compId}/image`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Image uploaded');
      fetchComponents();
      fetchChangeLogs();
    } catch (e) {
      toast.error('Upload failed: ' + (e.response?.data?.detail || e.message));
    }
  };

  const handleGLBUpload = async (compId, file) => {
    const fd = new FormData();
    fd.append('file', file);
    try {
      await axios.post(`${API}/admin/components/${compId}/glb`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('3D model uploaded');
      fetchComponents();
      fetchChangeLogs();
    } catch (e) {
      toast.error('GLB upload failed: ' + (e.response?.data?.detail || e.message));
    }
  };

  // Template handlers
  const handleEditTemplate = (template) => {
    setEditTemplate(template);
    setTemplateForm({
      id: template.id,
      name: template.name,
      desc: template.desc || '',
      budget: template.budget || 'Mid',
      price: template.price || 0,
      color: template.color || '#3b82f6',
      components: template.components || []
    });
    setShowTemplateEdit(true);
  };

  const handleSaveTemplate = async () => {
    try {
      if (editTemplate) {
        // Update existing template
        await axios.put(`${API}/admin/configurations/${templateForm.id}`, templateForm, { withCredentials: true });
        toast.success('Template updated');
      } else {
        // Create new template
        if (!templateForm.id) {
          // Auto-generate ID from name
          templateForm.id = templateForm.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
        }
        await axios.post(`${API}/admin/configurations`, templateForm, { withCredentials: true });
        toast.success('Template created');
      }
      setShowTemplateEdit(false);
      fetchTemplates();
      fetchChangeLogs();
    } catch (e) {
      toast.error('Template save failed: ' + (e.response?.data?.detail || e.message));
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Delete this template?')) return;
    try {
      await axios.delete(`${API}/admin/configurations/${templateId}`, { withCredentials: true });
      toast.success('Template deleted');
      fetchTemplates();
      fetchChangeLogs();
    } catch (e) {
      toast.error('Delete failed');
    }
  };

  const [uploadTarget, setUploadTarget] = useState(null);
  const [uploadType, setUploadType] = useState(null);

  const triggerUpload = (compId, type) => {
    setUploadTarget(compId);
    setUploadType(type);
    if (type === 'glb') glbInputRef.current?.click();
    else fileInputRef.current?.click();
  };

  const onFileSelected = (e) => {
    const file = e.target.files?.[0];
    if (!file || !uploadTarget) return;
    if (uploadType === 'glb') handleGLBUpload(uploadTarget, file);
    else handleImageUpload(uploadTarget, file);
    e.target.value = '';
    setUploadTarget(null);
  };

  // Texture/Color management
  const addTextureOption = () => {
    setForm(f => ({ ...f, textures: [...(f.textures || []), ''] }));
  };

  const updateTexture = (idx, val) => {
    setForm(f => {
      const textures = [...(f.textures || [])];
      textures[idx] = val;
      return { ...f, textures };
    });
  };

  const removeTexture = (idx) => {
    setForm(f => ({ ...f, textures: f.textures.filter((_, i) => i !== idx) }));
  };

  const addColorOption = () => {
    setForm(f => ({ ...f, colors: [...(f.colors || []), ''] }));
  };

  const updateColor = (idx, val) => {
    setForm(f => {
      const colors = [...(f.colors || [])];
      colors[idx] = val;
      return { ...f, colors };
    });
  };

  const removeColor = (idx) => {
    setForm(f => ({ ...f, colors: f.colors.filter((_, i) => i !== idx) }));
  };

  const formFields = (
    <div className="space-y-4 py-2">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">Component ID</label>
          <Input value={form.comp_id} onChange={e => setForm(f => ({ ...f, comp_id: e.target.value }))}
            className="bg-slate-800 border-white/10 text-white text-xs h-8"
            disabled={!!editComp} data-testid="admin-comp-id" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">Name</label>
          <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
            className="bg-slate-800 border-white/10 text-white text-xs h-8" data-testid="admin-comp-name" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">Category</label>
          <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
            className="w-full bg-slate-800 border border-white/10 rounded-md text-white text-xs h-8 px-2" data-testid="admin-comp-category">
            {CATEGORIES.map(c => (
              <option key={c.id} value={c.id}>{c.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">Subcategory</label>
          <Input value={form.subcategory} onChange={e => setForm(f => ({ ...f, subcategory: e.target.value }))}
            className="bg-slate-800 border-white/10 text-white text-xs h-8" data-testid="admin-comp-subcategory" />
        </div>
      </div>
      <div className="grid grid-cols-4 gap-3">
        <div>
          <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">Width</label>
          <Input type="number" value={form.grid_w} onChange={e => setForm(f => ({ ...f, grid_w: e.target.value }))}
            className="bg-slate-800 border-white/10 text-white text-xs h-8" data-testid="admin-comp-width" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">Height</label>
          <Input type="number" value={form.grid_h} onChange={e => setForm(f => ({ ...f, grid_h: e.target.value }))}
            className="bg-slate-800 border-white/10 text-white text-xs h-8" data-testid="admin-comp-height" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">Price (INR)</label>
          <Input type="number" value={form.price} onChange={e => setForm(f => ({ ...f, price: e.target.value }))}
            className="bg-slate-800 border-white/10 text-white text-xs h-8" data-testid="admin-comp-price" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">Weight (kg)</label>
          <Input type="number" value={form.weight} onChange={e => setForm(f => ({ ...f, weight: e.target.value }))}
            className="bg-slate-800 border-white/10 text-white text-xs h-8" data-testid="admin-comp-weight" />
        </div>
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">Description</label>
        <Input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
          className="bg-slate-800 border-white/10 text-white text-xs h-8" data-testid="admin-comp-desc" />
      </div>

      {/* Textures Section */}
      <div className="border-t border-white/5 pt-3">
        <div className="flex items-center justify-between mb-2">
          <label className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1">
            <Palette className="w-3 h-3" /> Texture Options
          </label>
          <Button size="sm" variant="ghost" onClick={addTextureOption} className="h-6 text-xs text-blue-400">
            <Plus className="w-3 h-3 mr-1" /> Add
          </Button>
        </div>
        <div className="space-y-2">
          {(form.textures || []).map((tex, idx) => (
            <div key={idx} className="flex gap-2">
              <Input value={tex} onChange={e => updateTexture(idx, e.target.value)} placeholder="e.g., matte, glossy"
                className="bg-slate-800 border-white/10 text-white text-xs h-7 flex-1" />
              <Button size="sm" variant="ghost" onClick={() => removeTexture(idx)} className="h-7 w-7 p-0 text-red-400">
                <X className="w-3 h-3" />
              </Button>
            </div>
          ))}
        </div>
      </div>

      {/* Colors Section */}
      <div className="border-t border-white/5 pt-3">
        <div className="flex items-center justify-between mb-2">
          <label className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1">
            <Palette className="w-3 h-3" /> Color Options
          </label>
          <Button size="sm" variant="ghost" onClick={addColorOption} className="h-6 text-xs text-blue-400">
            <Plus className="w-3 h-3 mr-1" /> Add
          </Button>
        </div>
        <div className="space-y-2">
          {(form.colors || []).map((col, idx) => (
            <div key={idx} className="flex gap-2">
              <Input value={col} onChange={e => updateColor(idx, e.target.value)} placeholder="e.g., black, tan, ivory"
                className="bg-slate-800 border-white/10 text-white text-xs h-7 flex-1" />
              <Button size="sm" variant="ghost" onClick={() => removeColor(idx)} className="h-7 w-7 p-0 text-red-400">
                <X className="w-3 h-3" />
              </Button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // Show loading while checking admin role
  if (isAdmin === null) {
    return (
      <div className="min-h-screen bg-[#020617] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-slate-400 text-sm">Verifying admin access...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#020617]" data-testid="admin-panel">
      {/* Hidden file inputs */}
      <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={onFileSelected} />
      <input ref={glbInputRef} type="file" accept=".glb,.gltf" className="hidden" onChange={onFileSelected} />

      {/* Header */}
      <header className="h-14 glass-panel flex items-center justify-between px-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/studio')} className="text-slate-400 hover:text-white">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <Settings className="w-5 h-5 text-amber-400" />
          <span className="font-chivo font-bold text-white">Admin Panel</span>
          <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 text-[10px]">
            {components.length} Components
          </Badge>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-slate-500">{user?.email}</span>
          <Button variant="ghost" size="sm" onClick={logout} className="text-slate-400 hover:text-white">
            <LogOut className="w-4 h-4" />
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <div className="p-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="bg-slate-800/50 mb-6">
            <TabsTrigger value="components" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white">
              <Package className="w-4 h-4 mr-2" /> Components ({components.length})
            </TabsTrigger>
            <TabsTrigger value="templates" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white">
              <LayoutGrid className="w-4 h-4 mr-2" /> Templates ({templates.length})
            </TabsTrigger>
            <TabsTrigger value="changelog" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white">
              <History className="w-4 h-4 mr-2" /> Change Log
            </TabsTrigger>
          </TabsList>

          {/* Components Tab */}
          <TabsContent value="components">
            {/* Filters */}
            <div className="flex items-center gap-4 mb-4">
              <Input 
                placeholder="Search components..." 
                value={searchTerm}
                onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                className="w-64 bg-slate-800 border-white/10 text-white text-xs h-9"
              />
              <select 
                value={filterCategory} 
                onChange={e => { setFilterCategory(e.target.value); setCurrentPage(1); }}
                className="bg-slate-800 border border-white/10 rounded-md text-white text-xs h-9 px-3"
              >
                <option value="all">All Categories</option>
                {CATEGORIES.map(c => (
                  <option key={c.id} value={c.id}>{c.label}</option>
                ))}
              </select>
              <div className="flex-1" />
              <Button onClick={() => { setForm({ comp_id: '', name: '', category: 'Seating', subcategory: '', grid_w: 2, grid_h: 2, price: 0, weight: 0, description: '', icon: 'box', textures: [], colors: [], materials: [] }); setShowAdd(true); }}
                className="bg-blue-600 hover:bg-blue-700 text-white h-9">
                <Plus className="w-4 h-4 mr-2" /> Add Component
              </Button>
            </div>

            {/* Components Table */}
            <div className="glass-card rounded-xl overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/5 bg-slate-800/50">
                    <th className="text-left py-3 px-4 text-[10px] uppercase tracking-wider text-slate-500">Component</th>
                    <th className="text-left py-3 px-4 text-[10px] uppercase tracking-wider text-slate-500">Category</th>
                    <th className="text-left py-3 px-4 text-[10px] uppercase tracking-wider text-slate-500">Size</th>
                    <th className="text-left py-3 px-4 text-[10px] uppercase tracking-wider text-slate-500">Price</th>
                    <th className="text-left py-3 px-4 text-[10px] uppercase tracking-wider text-slate-500">Weight</th>
                    <th className="text-left py-3 px-4 text-[10px] uppercase tracking-wider text-slate-500">Options</th>
                    <th className="text-right py-3 px-4 text-[10px] uppercase tracking-wider text-slate-500">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedComponents.map(comp => (
                    <tr key={comp.comp_id} className="border-b border-white/5 hover:bg-slate-800/30" data-testid={`admin-row-${comp.comp_id}`}>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-3">
                          {comp.image ? (
                            <img src={comp.image} alt="" className="w-10 h-10 rounded object-cover bg-slate-700" />
                          ) : (
                            <div className="w-10 h-10 rounded bg-slate-700 flex items-center justify-center">
                              <Package className="w-4 h-4 text-slate-500" />
                            </div>
                          )}
                          <div>
                            <span className="text-sm text-white font-medium">{comp.name}</span>
                            <span className="text-[10px] text-slate-600 block font-mono">{comp.comp_id}</span>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <Badge className="text-[10px] bg-slate-700 text-slate-300 border-none">{comp.category}</Badge>
                      </td>
                      <td className="py-3 px-4 text-xs text-slate-400 font-mono">{comp.grid_w}x{comp.grid_h}</td>
                      <td className="py-3 px-4 text-xs text-blue-400 font-mono">{formatPrice(comp.price)}</td>
                      <td className="py-3 px-4 text-xs text-slate-400">{comp.weight}kg</td>
                      <td className="py-3 px-4">
                        <div className="flex gap-1">
                          {(comp.textures?.length > 0 || comp.colors?.length > 0) && (
                            <Badge className="text-[9px] bg-purple-500/20 text-purple-400 border-purple-500/30">
                              {(comp.textures?.length || 0) + (comp.colors?.length || 0)} options
                            </Badge>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <div className="flex justify-end gap-2">
                          <Button size="sm" variant="ghost" onClick={() => triggerUpload(comp.comp_id, 'image')}
                            className="h-7 w-7 p-0 text-slate-400 hover:text-blue-400" title="Upload Image">
                            <Image className="w-3.5 h-3.5" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => triggerUpload(comp.comp_id, 'glb')}
                            className="h-7 w-7 p-0 text-slate-400 hover:text-purple-400" title="Upload 3D Model">
                            <Upload className="w-3.5 h-3.5" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => handleEdit(comp)}
                            className="h-7 w-7 p-0 text-slate-400 hover:text-amber-400" title="Edit">
                            <Edit2 className="w-3.5 h-3.5" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => handleDelete(comp.comp_id)}
                            className="h-7 w-7 p-0 text-slate-400 hover:text-red-400" title="Delete">
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-4">
                <span className="text-xs text-slate-500">
                  Showing {((currentPage - 1) * ITEMS_PER_PAGE) + 1}-{Math.min(currentPage * ITEMS_PER_PAGE, filteredComponents.length)} of {filteredComponents.length}
                </span>
                <div className="flex items-center gap-2">
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="h-8 border-slate-700 text-slate-400"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="text-xs text-slate-400 px-3">
                    Page {currentPage} of {totalPages}
                  </span>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="h-8 border-slate-700 text-slate-400"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </TabsContent>

          {/* Templates Tab */}
          <TabsContent value="templates">
            <div className="flex items-center justify-between mb-4">
              <span className="text-xs text-slate-500">{templates.length} templates available</span>
              <Button onClick={() => { setTemplateForm({ id: '', name: '', desc: '', budget: 'Mid', price: 0, color: '#3b82f6', components: [] }); setEditTemplate(null); setShowTemplateEdit(true); }}
                className="bg-blue-600 hover:bg-blue-700 text-white h-9" data-testid="add-template-btn">
                <Plus className="w-4 h-4 mr-2" /> Add Template
              </Button>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {templates.map(template => (
                <div key={template.id} className="glass-card rounded-xl p-4 hover:border-blue-500/30 transition-all" data-testid={`template-${template.id}`}>
                  <div className="flex items-start gap-3">
                    <div 
                      className="w-10 h-10 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: template.color + '20', border: `1px solid ${template.color}40` }}
                    >
                      <LayoutGrid className="w-5 h-5" style={{ color: template.color }} />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-white font-semibold text-sm">{template.name}</h3>
                      <p className="text-xs text-slate-500 mt-1">{template.desc}</p>
                      <div className="flex items-center gap-3 mt-2">
                        <span className="text-xs text-blue-400 font-mono">{formatPrice(template.price)}</span>
                        <span className="text-xs text-slate-600">{template.components?.length || 0} components</span>
                      </div>
                    </div>
                    <div className="flex flex-col gap-1">
                      <Button size="sm" variant="ghost" onClick={() => handleEditTemplate(template)} className="h-7 w-7 p-0 text-slate-400 hover:text-amber-400" data-testid={`edit-template-${template.id}`}>
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => handleDeleteTemplate(template.id)} className="h-7 w-7 p-0 text-slate-400 hover:text-red-400" data-testid={`delete-template-${template.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* Change Log Tab */}
          <TabsContent value="changelog">
            <div className="glass-card rounded-xl p-4">
              <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                <History className="w-4 h-4 text-blue-400" /> Recent Changes
              </h3>
              {changeLogs.length === 0 ? (
                <p className="text-slate-500 text-sm">No changes recorded yet.</p>
              ) : (
                <ScrollArea className="h-96">
                  <div className="space-y-3">
                    {changeLogs.map((log, idx) => (
                      <div key={idx} className="flex items-start gap-3 p-3 bg-slate-800/30 rounded-lg">
                        <div className={`w-2 h-2 rounded-full mt-1.5 ${
                          log.action === 'create' ? 'bg-green-400' : 
                          log.action === 'update' ? 'bg-amber-400' : 'bg-red-400'
                        }`} />
                        <div className="flex-1">
                          <p className="text-sm text-white">
                            <span className="capitalize text-slate-400">{log.action}</span> {log.entity_type}: <span className="text-blue-400">{log.entity_id}</span>
                          </p>
                          {log.changes && (
                            <p className="text-xs text-slate-500 mt-1">{JSON.stringify(log.changes)}</p>
                          )}
                          <p className="text-[10px] text-slate-600 mt-1">{log.timestamp} by {log.user_email}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Edit Component Dialog */}
      <Dialog open={showEdit} onOpenChange={setShowEdit}>
        <DialogContent className="bg-slate-900 border-white/10 max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Edit2 className="w-4 h-4 text-amber-400" /> Edit Component
            </DialogTitle>
          </DialogHeader>
          {formFields}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowEdit(false)} className="text-slate-400">Cancel</Button>
            <Button onClick={handleSaveEdit} className="bg-blue-600 hover:bg-blue-700">
              <Save className="w-4 h-4 mr-2" /> Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Component Dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="bg-slate-900 border-white/10 max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Plus className="w-4 h-4 text-blue-400" /> Add New Component
            </DialogTitle>
          </DialogHeader>
          {formFields}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowAdd(false)} className="text-slate-400">Cancel</Button>
            <Button onClick={handleAddNew} className="bg-blue-600 hover:bg-blue-700">
              <Plus className="w-4 h-4 mr-2" /> Add Component
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Template Dialog */}
      <Dialog open={showTemplateEdit} onOpenChange={setShowTemplateEdit}>
        <DialogContent className="bg-slate-900 border-white/10 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <LayoutGrid className="w-4 h-4 text-amber-400" /> {editTemplate ? 'Edit Template' : 'Add New Template'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {!editTemplate && (
              <div>
                <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">Template ID (auto-generated if empty)</label>
                <Input value={templateForm.id} onChange={e => setTemplateForm(f => ({ ...f, id: e.target.value }))}
                  placeholder="e.g., luxury-4-seater"
                  className="bg-slate-800 border-white/10 text-white text-xs h-8" data-testid="template-id-input" />
              </div>
            )}
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">Name</label>
              <Input value={templateForm.name} onChange={e => setTemplateForm(f => ({ ...f, name: e.target.value }))}
                className="bg-slate-800 border-white/10 text-white text-xs h-8" data-testid="template-name-input" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">Description</label>
              <Input value={templateForm.desc} onChange={e => setTemplateForm(f => ({ ...f, desc: e.target.value }))}
                className="bg-slate-800 border-white/10 text-white text-xs h-8" data-testid="template-desc-input" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">Budget</label>
                <select value={templateForm.budget} onChange={e => setTemplateForm(f => ({ ...f, budget: e.target.value }))}
                  className="w-full bg-slate-800 border border-white/10 rounded-md text-white text-xs h-8 px-2" data-testid="template-budget-select">
                  <option value="Budget">Budget</option>
                  <option value="Mid">Mid</option>
                  <option value="Premium">Premium</option>
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">Price</label>
                <Input type="number" value={templateForm.price} onChange={e => setTemplateForm(f => ({ ...f, price: Number(e.target.value) }))}
                  className="bg-slate-800 border-white/10 text-white text-xs h-8" data-testid="template-price-input" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">Color</label>
                <Input type="color" value={templateForm.color} onChange={e => setTemplateForm(f => ({ ...f, color: e.target.value }))}
                  className="bg-slate-800 border-white/10 h-8 w-full p-1" data-testid="template-color-input" />
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">Components ({templateForm.components?.length || 0})</label>
              <p className="text-xs text-slate-500">To add components, create the template first, then design it in the Design Studio and save as this template.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowTemplateEdit(false)} className="text-slate-400" data-testid="template-cancel-btn">Cancel</Button>
            <Button onClick={handleSaveTemplate} className="bg-blue-600 hover:bg-blue-700" data-testid="template-save-btn">
              <Save className="w-4 h-4 mr-2" /> {editTemplate ? 'Save Template' : 'Create Template'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
